import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Plus, Trash2, Save, X, ExternalLink, ShieldCheck, Pause, Play, Globe,
  Edit3, Phone, Mail, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { studioListSites, studioCreateSite, studioUpdateSite, studioDeleteSite } from "../api";
import { T, CARD, INP } from "./theme";

const btnGold = { background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" };
const btnGhost = { border: `1px solid ${T.border}`, color: T.text, background: "transparent" };

const emptySite = {
  name: "", slug: "", website_url: "", admin_url: "",
  contact_email: "", contact_phone: "", industry: "", notes: "", status: "active",
};

const STATUS_COLOR = { active: T.success, suspended: T.warning, archived: T.textFaint };

export default function ClientSitesPanel() {
  const [sites, setSites] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => studioListSites().then(setSites).catch(() => setSites([]));
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      if (edit.__new) await studioCreateSite(edit);
      else await studioUpdateSite(edit.id, edit);
      toast.success("Site saved"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try { await studioDeleteSite(s.id); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async (s) => {
    const next = s.status === "active" ? "suspended" : "active";
    try { await studioUpdateSite(s.id, { status: next }); toast.success(next === "active" ? "Restored" : "Suspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Copied"); };

  if (!sites) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;

  return (
    <div data-testid="panel-client-sites">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display font-bold tracking-tight text-2xl md:text-3xl" style={{ color: T.text }}>Client websites</h2>
          <p className="text-sm mt-1" style={{ color: T.textDim }}>
            Master directory for every Yosi Media Studio site. Suspend, restore or launch admin from here.
          </p>
        </div>
        <button onClick={() => setEdit({ ...emptySite, __new: true })} data-testid="site-add"
          className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-sm" style={btnGold}>
          <Plus size={16} /> Add client
        </button>
      </div>

      <div className="grid gap-3">
        {sites.map((s) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            data-testid="site-card" className="rounded-2xl p-5" style={CARD}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: T.goldSoft, color: T.gold }}>
                <Globe size={19} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-lg truncate" style={{ color: T.text }}>{s.name}</h3>
                  {s.is_primary && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest"
                      style={{ background: T.goldSoft, color: T.gold }}>Primary</span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest"
                    style={{ background: `${STATUS_COLOR[s.status] || T.textFaint}22`, color: STATUS_COLOR[s.status] || T.textFaint }}>
                    {s.status}
                  </span>
                </div>
                <div className="text-xs mt-1" style={{ color: T.textDim }}>{s.industry || "—"}</div>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs" style={{ color: T.textDim }}>
                  <a href={s.website_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:underline" style={{ color: T.gold }}>
                    <ExternalLink size={12} /> {s.website_url}
                  </a>
                  <a href={s.admin_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:underline" style={{ color: T.gold }}>
                    <ShieldCheck size={12} /> Open admin
                  </a>
                  {s.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {s.contact_phone}</span>}
                  {s.contact_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {s.contact_email}</span>}
                </div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => copyLink(s.admin_url)} title="Copy admin link"
                  className="p-2 rounded-lg" style={btnGhost}><Copy size={15} /></button>
                <button onClick={() => setEdit({ ...s })} title="Edit"
                  className="p-2 rounded-lg" style={btnGhost}><Edit3 size={15} /></button>
                <button onClick={() => toggleStatus(s)} title={s.status === "active" ? "Suspend" : "Restore"}
                  className="p-2 rounded-lg" style={btnGhost}>
                  {s.status === "active" ? <Pause size={15} /> : <Play size={15} />}
                </button>
                {!s.is_primary && (
                  <button onClick={() => remove(s)} title="Remove" className="p-2 rounded-lg" style={{ color: T.danger }}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
            {s.notes && <p className="mt-3 text-xs" style={{ color: T.textFaint }}>{s.notes}</p>}
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 rounded-xl text-xs flex items-start gap-3" style={{ ...CARD, background: T.card2 }}>
        <ShieldCheck size={16} style={{ color: T.gold }} className="mt-0.5" />
        <div style={{ color: T.textDim }}>
          <strong style={{ color: T.text }}>How adding a new client works:</strong> Each new site gets its own Emergent
          deployment (isolated DB + hosting). After you deploy the new client site, add it here with its public URL and
          `/admin` URL. This dashboard serves as your unified launcher and passwords vault; suspending a client here
          hides them from your directory (each client's own portal remains under your operational control).
        </div>
      </div>

      <AnimatePresence>
        {edit && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
            onClick={() => setEdit(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} data-testid="site-editor"
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-6" style={CARD}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display font-bold text-lg" style={{ color: T.text }}>
                  {edit.__new ? "Add client website" : `Edit ${edit.name}`}
                </h3>
                <button onClick={() => setEdit(null)} className="p-2 rounded-lg" style={btnGhost}><X size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["name", "Business name", 2],
                  ["slug", "Slug (short id)", 1],
                  ["industry", "Industry", 1],
                  ["website_url", "Public website URL", 2],
                  ["admin_url", "Admin portal URL", 2],
                  ["contact_phone", "Contact phone", 1],
                  ["contact_email", "Contact email", 1],
                ].map(([k, label, span]) => (
                  <div key={k} className={span === 2 ? "col-span-2" : ""}>
                    <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
                    <input value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                      data-testid={`site-${k}`} className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>Status</label>
                  <select value={edit.status || "active"} onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} data-testid="site-status">
                    <option value="active" style={{ background: T.card }}>Active</option>
                    <option value="suspended" style={{ background: T.card }}>Suspended</option>
                    <option value="archived" style={{ background: T.card }}>Archived</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>Internal notes</label>
                  <textarea rows={3} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
                </div>
              </div>
              <button onClick={save} disabled={saving} data-testid="site-save"
                className="mt-5 w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                style={btnGold}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save site
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
